/**
 * UserManual — long-scroll in-app rendering of /public/manual/user-manual.md.
 *
 * Features:
 *  - react-markdown + remark-gfm (tables, task-lists, strikethrough)
 *  - rehype-slug + rehype-autolink-headings (deep-linkable section anchors)
 *  - Sticky left-rail Table of Contents built from H2/H3 headings
 *  - Image path rewriting `img/user-manual/…` → `/manual/…`
 *  - Client brand logo in the header
 *  - "Print / save as PDF" button → /help/user-manual/print
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSlug from "rehype-slug";
import rehypeAutolinkHeadings from "rehype-autolink-headings";
import { Printer, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/lib/auth";

const MANUAL_URL = "/manual/user-manual.md";

function slugify(s) {
  return String(s)
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

function extractToc(md) {
  const lines = md.split("\n");
  const items = [];
  let inFence = false;
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (/^```/.test(line)) inFence = !inFence;
    if (inFence) continue;
    const m = /^(#{2,3})\s+(.+?)\s*$/.exec(line);
    if (!m) continue;
    const depth = m[1].length; // 2 or 3
    const text = m[2].replace(/`([^`]+)`/g, "$1"); // strip inline code
    items.push({ depth, text, id: slugify(text) });
  }
  return items;
}

function useBrandClient() {
  const { assignments } = useAuth();
  const [brand, setBrand] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const clientId = assignments?.find((a) => a.client_id)?.client_id;
      if (!clientId) return;
      const { data } = await supabase
        .from("lbs_clients")
        .select("id,name,brand_logo_url,brand_primary_color")
        .eq("id", clientId)
        .maybeSingle();
      if (!cancelled) setBrand(data || null);
    })();
    return () => { cancelled = true; };
  }, [assignments]);
  return brand;
}

const markdownComponents = {
  img: ({ src = "", alt = "", ...rest }) => {
    // Rewrite relative image references so they resolve under /manual/
    const rewritten = src.startsWith("img/user-manual/")
      ? "/manual/" + src.replace(/^img\/user-manual\//, "")
      : src;
    return (
      <img
        src={rewritten}
        alt={alt}
        loading="lazy"
        className="my-4 rounded-md border border-slate-200 shadow-sm max-w-full"
        {...rest}
      />
    );
  },
  table: ({ children, ...rest }) => (
    <div className="my-4 overflow-x-auto">
      <table className="min-w-full text-sm border border-slate-200" {...rest}>
        {children}
      </table>
    </div>
  ),
  th: ({ children, ...rest }) => (
    <th className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-left font-medium" {...rest}>
      {children}
    </th>
  ),
  td: ({ children, ...rest }) => (
    <td className="border-b border-slate-100 px-3 py-2 align-top" {...rest}>
      {children}
    </td>
  ),
  code: ({ inline, className, children, ...rest }) => {
    if (inline) {
      return (
        <code className="rounded bg-slate-100 px-1 py-0.5 text-[0.85em] font-mono" {...rest}>
          {children}
        </code>
      );
    }
    return (
      <code className={className} {...rest}>
        {children}
      </code>
    );
  },
  blockquote: ({ children, ...rest }) => (
    <blockquote
      className="my-3 border-l-4 border-slate-300 bg-slate-50 px-3 py-2 text-slate-700"
      {...rest}
    >
      {children}
    </blockquote>
  ),
};

export default function UserManual() {
  const [md, setMd] = useState("");
  const [err, setErr] = useState(null);
  const brand = useBrandClient();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(MANUAL_URL, { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const text = await res.text();
        if (!cancelled) setMd(text);
      } catch (e) {
        if (!cancelled) setErr(e.message || "Failed to load manual");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toc = useMemo(() => (md ? extractToc(md) : []), [md]);

  return (
    <div className="mx-auto max-w-screen-2xl px-4 sm:px-6 py-6" data-testid="user-manual-page">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-4 mb-6">
        <div className="flex items-center gap-3">
          {brand?.brand_logo_url ? (
            <img
              src={brand.brand_logo_url}
              alt={`${brand.name} logo`}
              className="h-10 w-10 rounded object-contain bg-white border border-slate-200"
              data-testid="manual-brand-logo"
            />
          ) : (
            <div className="rounded bg-slate-100 p-2">
              <FileText className="h-6 w-6 text-slate-500" />
            </div>
          )}
          <div>
            <h1 className="text-lg font-semibold text-slate-900">
              {brand?.name ? `${brand.name} · ` : ""}User manual
            </h1>
            <p className="text-xs text-slate-500">
              A day-in-the-life guide covering site setup, bookings and the Intake Form flow.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/help/user-manual/print" target="_blank" rel="noopener">
            <Button variant="outline" size="sm" data-testid="manual-print-btn">
              <Printer className="mr-2 h-4 w-4" /> Print / save as PDF
            </Button>
          </Link>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Table of Contents */}
        <aside className="lg:sticky lg:top-24 lg:h-[calc(100vh-8rem)] lg:w-72 lg:shrink-0 lg:overflow-y-auto">
          <nav
            className="text-sm border border-slate-200 rounded-md bg-white p-3"
            data-testid="manual-toc"
          >
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">
              Contents
            </div>
            {toc.length === 0 && (
              <div className="text-xs text-slate-400">Loading…</div>
            )}
            <ul className="space-y-1">
              {toc.map((t) => (
                <li key={t.id} className={t.depth === 3 ? "pl-3" : ""}>
                  <a
                    href={`#${t.id}`}
                    className="block rounded px-1.5 py-1 text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition"
                  >
                    {t.text}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0">
          {err ? (
            <div className="rounded border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
              Couldn't load the manual: {err}
            </div>
          ) : (
            <article className="prose-manual" data-testid="manual-content">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[
                  rehypeSlug,
                  [rehypeAutolinkHeadings, { behavior: "wrap" }],
                ]}
                components={markdownComponents}
              >
                {md}
              </ReactMarkdown>
            </article>
          )}
        </main>
      </div>

      {/* Minimal manual typography — scoped to .prose-manual to avoid a full Tailwind Typography install */}
      <style>{`
        .prose-manual { color: #1e293b; line-height: 1.65; font-size: 15px; }
        .prose-manual h1 { font-size: 1.75rem; font-weight: 700; margin: 1.5rem 0 0.75rem; color: #0f172a; }
        .prose-manual h2 { font-size: 1.35rem; font-weight: 700; margin: 2rem 0 0.75rem; padding-bottom: 0.35rem; border-bottom: 1px solid #e2e8f0; color: #0f172a; scroll-margin-top: 5rem; }
        .prose-manual h3 { font-size: 1.10rem; font-weight: 600; margin: 1.35rem 0 0.5rem; color: #0f172a; scroll-margin-top: 5rem; }
        .prose-manual h4 { font-size: 1.00rem; font-weight: 600; margin: 1rem 0 0.35rem; color: #334155; }
        .prose-manual p  { margin: 0.55rem 0; }
        .prose-manual ul { margin: 0.5rem 0 0.75rem 1.25rem; list-style: disc; }
        .prose-manual ol { margin: 0.5rem 0 0.75rem 1.25rem; list-style: decimal; }
        .prose-manual li { margin: 0.2rem 0; }
        .prose-manual strong { color: #0f172a; }
        .prose-manual a { color: #1d4ed8; text-decoration: none; }
        .prose-manual a:hover { text-decoration: underline; }
        .prose-manual hr { margin: 2rem 0; border: 0; border-top: 1px solid #e2e8f0; }
        .prose-manual h2 a, .prose-manual h3 a { color: inherit; text-decoration: none; }
      `}</style>
    </div>
  );
}
